import {
  DEFAULT_MAX_OPTIONAL_DOCUMENTS,
  type FileEntryCondition,
  type FormSection,
  type SelectOption,
  type StepFormBranch,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowVersion,
} from '@/api/workflows';
import { resolveDateFormat } from '@/form-renderer/dateFormat';

/**
 * Structural diff between two frozen workflow-step lists. Two versions of the
 * same workflow are compared by walking the trees keyed on the stable ids
 * emitted by the backend (`WorkflowStep.id`, `FormSection.id`, `FormRow.id`,
 * `FormColumn.id`, `WorkflowField.id`).
 *
 * A step / section / field that keeps its id across a version bump is treated
 * as the SAME entity (its content diff is a `modified` entry). Delete-and-add
 * on the same slot produces a paired `removed` + `added` — because the
 * originator effectively created a new entity, which is closer to how the
 * approver reads the change.
 *
 * The result is grouped BY STEP: each returned `StepChange` carries the
 * per-section + per-field changes below it. Unchanged branches are pruned so
 * the panel that renders this only shows what the approver actually needs to
 * look at.
 */

export type ChangeKind = 'added' | 'removed' | 'modified';

/**
 * One property that changed between two entities (step, section, or field).
 * `key` is machine-facing (drives an i18n string in the panel);
 * `before`/`after` are already-humanized strings the panel renders verbatim.
 */
export type PropertyChange = {
  key: string;
  before?: string;
  after?: string;
};

export type FieldChange = {
  fieldId: string;
  labelEn: string;
  labelFr: string;
  type: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
};

export type SectionChange = {
  sectionId: string;
  titleEn: string;
  titleFr: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
  fieldChanges: FieldChange[];
};

export type StepChange = {
  stepId: string;
  titleEn: string;
  titleFr: string;
  type: string;
  kind: ChangeKind;
  propertyChanges: PropertyChange[];
  sectionChanges: SectionChange[];
};

export type WorkflowDiff = {
  steps: StepChange[];
  hasChanges: boolean;
};

/** Top-level entry point. Diff two frozen step lists (previous vs current). */
export function diffWorkflowSteps(
  prev: readonly WorkflowStep[],
  next: readonly WorkflowStep[],
): WorkflowDiff {
  const prevById = new Map(prev.map((s) => [s.id, s]));
  const nextIds = new Set(next.map((s) => s.id));
  const steps: StepChange[] = [];

  // Walk NEXT first so unchanged/modified/added steps show up in the order
  // the approver sees them in the workflow itself.
  for (const nextStep of next) {
    const prevStep = prevById.get(nextStep.id);
    if (!prevStep) {
      steps.push(makeAddedStep(nextStep));
      continue;
    }
    const change = diffStep(prevStep, nextStep);
    if (change) steps.push(change);
  }
  // Then removed steps in their original order.
  for (const prevStep of prev) {
    if (!nextIds.has(prevStep.id)) {
      steps.push(makeRemovedStep(prevStep));
    }
  }

  return { steps, hasChanges: steps.length > 0 };
}

/** The versioned workflow-level settings a version freezes alongside its steps. */
export type WorkflowSettingsSnapshot = Pick<
  WorkflowVersion,
  'packageFilenameTemplate' | 'dateFormat' | 'reviewLayout' | 'formatPhoneNumbers'
>;

/**
 * Workflow-level settings that changed between two versions. `prev` is null on a first
 * submission, where every setting the new version carries reads as added. Unset and blank
 * are the same "not set" and never register as a change.
 */
export function diffWorkflowSettings(
  prev: WorkflowSettingsSnapshot | null,
  next: WorkflowSettingsSnapshot,
): PropertyChange[] {
  const changes: PropertyChange[] = [];
  const before = prev?.packageFilenameTemplate || '';
  const after = next.packageFilenameTemplate || '';
  if (before !== after) changes.push({ key: 'packageFilenameTemplate', before, after });
  // Compared as the EFFECTIVE format: unset means DD/MM/YYYY, so a legacy version (unset) and
  // one that picked DD/MM/YYYY explicitly present identically and are not a change. On a first
  // submission there is no previous form, so any format is reported.
  const nextFormat = resolveDateFormat(next.dateFormat);
  const prevFormat = prev ? resolveDateFormat(prev.dateFormat) : '';
  if (prevFormat !== nextFormat) {
    changes.push({ key: 'dateFormat', before: prevFormat, after: nextFormat });
  }
  // Opt-in presentation switches: unset is "off", so only a real flip is a change.
  const layoutBefore = prev?.reviewLayout ?? '';
  const layoutAfter = next.reviewLayout ?? '';
  if (layoutBefore !== layoutAfter) {
    changes.push({ key: 'reviewLayout', before: layoutBefore, after: layoutAfter });
  }
  const phonesBefore = prev?.formatPhoneNumbers === true ? '✓' : '';
  const phonesAfter = next.formatPhoneNumbers === true ? '✓' : '';
  if (phonesBefore !== phonesAfter) {
    changes.push({ key: 'formatPhoneNumbers', before: phonesBefore, after: phonesAfter });
  }
  return changes;
}

// ─── step diff ──────────────────────────────────────────────────────────────

function diffStep(prev: WorkflowStep, next: WorkflowStep): StepChange | null {
  const propertyChanges = diffStepProperties(prev, next);
  const sectionChanges = diffSections(prev.sections ?? [], next.sections ?? []);
  if (propertyChanges.length === 0 && sectionChanges.length === 0) return null;
  return {
    stepId: next.id,
    titleEn: next.titleEn,
    titleFr: next.titleFr,
    type: next.type,
    kind: 'modified',
    propertyChanges,
    sectionChanges,
  };
}

function makeAddedStep(step: WorkflowStep): StepChange {
  // Enumerate every section + field inside so the approver sees the full
  // "everything is new" tree. Same helper drives the v1 "first submission"
  // panel (parent passes prev=[] to diffWorkflowSteps) and the case where
  // a genuinely-new step is added between v2 and v3 — both want the same
  // depth of detail.
  return {
    stepId: step.id,
    titleEn: step.titleEn,
    titleFr: step.titleFr,
    type: step.type,
    kind: 'added',
    propertyChanges: [],
    sectionChanges: (step.sections ?? []).map(makeAddedSection),
  };
}

function makeAddedSection(section: FormSection): SectionChange {
  return {
    sectionId: section.id,
    titleEn: section.titleEn,
    titleFr: section.titleFr,
    kind: 'added',
    propertyChanges: [],
    fieldChanges: collectFields(section).map(makeAddedField),
  };
}

function makeAddedField(field: WorkflowField): FieldChange {
  return {
    fieldId: field.id,
    labelEn: field.labelEn,
    labelFr: field.labelFr,
    type: field.type,
    kind: 'added',
    propertyChanges: [],
  };
}

function makeRemovedStep(step: WorkflowStep): StepChange {
  return {
    stepId: step.id,
    titleEn: step.titleEn,
    titleFr: step.titleFr,
    type: step.type,
    kind: 'removed',
    propertyChanges: [],
    sectionChanges: (step.sections ?? []).map(makeRemovedSection),
  };
}

function makeRemovedSection(section: FormSection): SectionChange {
  return {
    sectionId: section.id,
    titleEn: section.titleEn,
    titleFr: section.titleFr,
    kind: 'removed',
    propertyChanges: [],
    fieldChanges: collectFields(section).map(makeRemovedField),
  };
}

function makeRemovedField(field: WorkflowField): FieldChange {
  return {
    fieldId: field.id,
    labelEn: field.labelEn,
    labelFr: field.labelFr,
    type: field.type,
    kind: 'removed',
    propertyChanges: [],
  };
}

function diffStepProperties(prev: WorkflowStep, next: WorkflowStep): PropertyChange[] {
  const changes: PropertyChange[] = [];
  pushIfChanged(changes, 'type', prev.type, next.type);
  pushIfChanged(changes, 'titleEn', prev.titleEn, next.titleEn);
  pushIfChanged(changes, 'titleFr', prev.titleFr, next.titleFr);
  // The rail / Application Review label. A row of its own because it changes what the advisor
  // reads in two places the page heading never appears. Both sides coalesce to '' so a version
  // predating the field shows no spurious row — same convention as `advisorOnly` below.
  pushIfChanged(changes, 'navTitleEn', prev.navTitleEn ?? '', next.navTitleEn ?? '');
  pushIfChanged(changes, 'navTitleFr', prev.navTitleFr ?? '', next.navTitleFr ?? '');
  pushIfChanged(changes, 'descriptionEn', prev.descriptionEn, next.descriptionEn);
  pushIfChanged(changes, 'descriptionFr', prev.descriptionFr, next.descriptionFr);
  // Step-type-specific configs — each is only meaningful on ONE step type,
  // but a change to any of them belongs on the diff. Serialization via
  // JSON.stringify keeps the check thorough without exploding every leaf
  // property into its own change row; approvers want the "PDF templates
  // changed" signal, they can drill into the step for detail.
  pushJsonChange(changes, 'fileEntries', prev.fileEntries ?? [], next.fileEntries ?? []);
  pushJsonChange(
    changes,
    'additionalDocumentEntries',
    prev.additionalDocumentEntries ?? [],
    next.additionalDocumentEntries ?? [],
  );
  pushIfChanged(
    changes,
    'optionalDocumentExtensions',
    (prev.optionalDocumentExtensions ?? []).slice().sort().join(', '),
    (next.optionalDocumentExtensions ?? []).slice().sort().join(', '),
  );
  pushIfChanged(
    changes,
    'optionalDocumentMaxFileSizeMb',
    prev.optionalDocumentMaxFileSizeMb ?? null,
    next.optionalDocumentMaxFileSizeMb ?? null,
  );
  pushIfChanged(
    changes,
    'showOptionalDocumentsSection',
    prev.showOptionalDocumentsSection ?? true,
    next.showOptionalDocumentsSection ?? true,
  );
  // Whether the step is withheld from the public customer runtime. Worth a change row of its
  // own: flipping it adds or removes a whole step from what a client sees, which is a review
  // decision rather than an editorial tweak. Both sides fall back to the runtime default
  // (false — visible to everyone) so a version predating the flag shows no spurious row.
  pushIfChanged(changes, 'advisorOnly', prev.advisorOnly ?? false, next.advisorOnly ?? false);
  // The three document caps. Each falls back to the SAME default the runtime applies — null for
  // the two combined caps ("no cap"), the legacy ten for the count — so a template that predates
  // these fields does not show three spurious change rows the first time it is compared.
  pushIfChanged(
    changes,
    'mandatoryCombinedMaxSizeMb',
    prev.mandatoryCombinedMaxSizeMb ?? null,
    next.mandatoryCombinedMaxSizeMb ?? null,
  );
  pushIfChanged(
    changes,
    'optionalCombinedMaxSizeMb',
    prev.optionalCombinedMaxSizeMb ?? null,
    next.optionalCombinedMaxSizeMb ?? null,
  );
  pushIfChanged(
    changes,
    'maxOptionalDocuments',
    prev.maxOptionalDocuments ?? DEFAULT_MAX_OPTIONAL_DOCUMENTS,
    next.maxOptionalDocuments ?? DEFAULT_MAX_OPTIONAL_DOCUMENTS,
  );
  pushJsonChange(changes, 'oneSpanConfig', prev.oneSpanConfig ?? null, next.oneSpanConfig ?? null);
  pushJsonChange(changes, 'welcomeConfig', prev.welcomeConfig ?? null, next.welcomeConfig ?? null);
  pushJsonChange(
    changes,
    'thankYouConfig',
    prev.thankYouConfig ?? null,
    next.thankYouConfig ?? null,
  );
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  // Its own row, never merged with the visibility one above — the two rules have
  // opposite polarity and opposite data behaviour, so an approver has to see which
  // of them moved.
  pushConditionsChange(
    changes,
    'disableConditions',
    prev.disableConditions ?? [],
    next.disableConditions ?? [],
    prev.disableConditionCombinator ?? null,
    next.disableConditionCombinator ?? null,
  );
  // Form branching — far the largest thing a step property can change. Adding, removing
  // or repointing one of these swaps every step below this one for another form's, so an
  // approver must see it as its own row rather than inferring it from a step list that
  // looks untouched.
  pushIfChanged(
    changes,
    'formBranches',
    summariseFormBranches(prev.formBranches ?? []),
    summariseFormBranches(next.formBranches ?? []),
  );
  return changes;
}

/** One line per rule: the answer that triggers it, the form it loads, and whether that
 *  form's opening step is merged into this one. Order-preserving — the runtime takes the
 *  first match in document order, so a reorder really is a change. */
function summariseFormBranches(branches: readonly StepFormBranch[]): string {
  return branches
    .map(
      (b) =>
        `${b.fieldId}=${b.optionValue} → ${b.targetTemplateId}${b.appendFirstStep ? ' (+first step)' : ''}`,
    )
    .join('\n');
}

// ─── section diff ───────────────────────────────────────────────────────────

function diffSections(prev: readonly FormSection[], next: readonly FormSection[]): SectionChange[] {
  const prevById = new Map(prev.map((s) => [s.id, s]));
  const nextIds = new Set(next.map((s) => s.id));
  const changes: SectionChange[] = [];

  for (const nextSection of next) {
    const prevSection = prevById.get(nextSection.id);
    if (!prevSection) {
      changes.push(makeAddedSection(nextSection));
      continue;
    }
    const propertyChanges = diffSectionProperties(prevSection, nextSection);
    const fieldChanges = diffFields(collectFields(prevSection), collectFields(nextSection));
    if (propertyChanges.length === 0 && fieldChanges.length === 0) continue;
    changes.push({
      sectionId: nextSection.id,
      titleEn: nextSection.titleEn,
      titleFr: nextSection.titleFr,
      kind: 'modified',
      propertyChanges,
      fieldChanges,
    });
  }
  for (const prevSection of prev) {
    if (!nextIds.has(prevSection.id)) {
      changes.push(makeRemovedSection(prevSection));
    }
  }
  return changes;
}

/** Order-insensitive serialization of a section's instance-count handles, for
 *  the same reason {@link serializePdfMappings} is: the diff should flag an
 *  add / remove / retarget, not a reordering. The language scope is part of the
 *  identity — an English and a French handle on the same box are two mappings. */
function serializeCountPdfMappings(s: FormSection): string {
  return (s.countPdfMappings ?? [])
    .map((m) => `${m.pdfEntryId}:${m.acroFieldName}:${m.language ?? ''}`)
    .sort()
    .join('|');
}

function diffSectionProperties(prev: FormSection, next: FormSection): PropertyChange[] {
  const changes: PropertyChange[] = [];
  pushIfChanged(changes, 'titleEn', prev.titleEn, next.titleEn);
  pushIfChanged(changes, 'titleFr', prev.titleFr, next.titleFr);
  pushIfChanged(changes, 'repeatable', prev.repeatable ?? false, next.repeatable ?? false);
  pushIfChanged(
    changes,
    'hideEditInReview',
    prev.hideEditInReview ?? false,
    next.hideEditInReview ?? false,
  );
  if ((prev.repeatable ?? false) || (next.repeatable ?? false)) {
    pushIfChanged(changes, 'minInstances', prev.minInstances ?? 1, next.minInstances ?? 1);
    pushIfChanged(changes, 'maxInstances', prev.maxInstances ?? null, next.maxInstances ?? null);
    pushIfChanged(
      changes,
      'instanceLabelEn',
      prev.instanceLabelEn ?? '',
      next.instanceLabelEn ?? '',
    );
    pushIfChanged(
      changes,
      'instanceLabelFr',
      prev.instanceLabelFr ?? '',
      next.instanceLabelFr ?? '',
    );
    pushIfChanged(
      changes,
      'repeatWithSectionId',
      prev.repeatWithSectionId ?? null,
      next.repeatWithSectionId ?? null,
    );
    // Boxes printing this section's instance count. Invisible to the advisor
    // and absent from the form preview — this diff is the ONLY place an
    // approver can see one come or go, which is why it is here rather than
    // left out as mapper bookkeeping.
    pushIfChanged(
      changes,
      'countPdfMappings',
      serializeCountPdfMappings(prev),
      serializeCountPdfMappings(next),
    );
    // Changes what BMO is told about the application (appInfo.jointApplication),
    // while changing nothing an approver can see in the form preview — so like
    // the count handles above, this diff is the only place it surfaces.
    pushIfChanged(
      changes,
      'jointApplicationSelector',
      prev.jointApplicationSelector ?? false,
      next.jointApplicationSelector ?? false,
    );
    // Invisible in the preview for the same reason the flag is, and it can INVERT
    // what the flag means — 0 reports joint on the first filled instance where 1
    // reports it on the second — so it has to surface here or the change ships
    // with nothing for an approver to read.
    pushIfChanged(
      changes,
      'jointApplicationThreshold',
      prev.jointApplicationThreshold ?? 1,
      next.jointApplicationThreshold ?? 1,
    );
  }
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  // Its own row, never merged with the visibility one above — the two rules have
  // opposite polarity and opposite data behaviour, so an approver has to see which
  // of them moved.
  pushConditionsChange(
    changes,
    'disableConditions',
    prev.disableConditions ?? [],
    next.disableConditions ?? [],
    prev.disableConditionCombinator ?? null,
    next.disableConditionCombinator ?? null,
  );
  return changes;
}

// ─── field diff ─────────────────────────────────────────────────────────────

function collectFields(section: FormSection): WorkflowField[] {
  const out: WorkflowField[] = [];
  for (const row of section.rows ?? []) {
    for (const col of row.columns ?? []) {
      if (col.field) out.push(col.field);
    }
  }
  return out;
}

function diffFields(prev: WorkflowField[], next: WorkflowField[]): FieldChange[] {
  const prevById = new Map(prev.map((f) => [f.id, f]));
  const nextIds = new Set(next.map((f) => f.id));
  const changes: FieldChange[] = [];
  for (const nextField of next) {
    const prevField = prevById.get(nextField.id);
    if (!prevField) {
      changes.push(makeAddedField(nextField));
      continue;
    }
    const propertyChanges = diffFieldProperties(prevField, nextField);
    if (propertyChanges.length === 0) continue;
    changes.push({
      fieldId: nextField.id,
      labelEn: nextField.labelEn,
      labelFr: nextField.labelFr,
      type: nextField.type,
      kind: 'modified',
      propertyChanges,
    });
  }
  for (const prevField of prev) {
    if (!nextIds.has(prevField.id)) {
      changes.push(makeRemovedField(prevField));
    }
  }
  return changes;
}

/** Stable, order-insensitive serialization of a field's PDF mappings so the
 *  diff flags an add/remove/retarget but not a mere reordering. `instanceIndex`
 *  is part of the identity — an edit that only re-routes a mapping to a
 *  different repeatable-section row must not be silently swallowed. */
function serializePdfMappings(f: WorkflowField): string {
  return (f.pdfMappings ?? [])
    .map((m) => `${m.pdfEntryId}:${m.acroFieldName}:${m.instanceIndex ?? ''}`)
    .sort()
    .join('|');
}

function diffFieldProperties(prev: WorkflowField, next: WorkflowField): PropertyChange[] {
  const changes: PropertyChange[] = [];
  // Identity / core.
  pushIfChanged(changes, 'type', prev.type, next.type);
  pushIfChanged(changes, 'labelEn', prev.labelEn, next.labelEn);
  pushIfChanged(changes, 'labelFr', prev.labelFr, next.labelFr);
  pushIfChanged(changes, 'required', prev.required, next.required);
  pushIfChanged(changes, 'placeholder', prev.placeholder ?? '', next.placeholder ?? '');
  // Length / numeric ranges.
  pushIfChanged(changes, 'minLength', prev.minLength ?? null, next.minLength ?? null);
  pushIfChanged(changes, 'maxLength', prev.maxLength ?? null, next.maxLength ?? null);
  pushIfChanged(changes, 'min', prev.min ?? null, next.min ?? null);
  pushIfChanged(changes, 'max', prev.max ?? null, next.max ?? null);
  pushIfChanged(changes, 'step', prev.step ?? null, next.step ?? null);
  pushIfChanged(changes, 'integerOnly', prev.integerOnly ?? false, next.integerOnly ?? false);
  // Validation + regex companions.
  pushIfChanged(changes, 'validate', prev.validate ?? '', next.validate ?? '');
  pushIfChanged(changes, 'regexPattern', prev.regexPattern ?? '', next.regexPattern ?? '');
  pushIfChanged(changes, 'regexMessageEn', prev.regexMessageEn ?? '', next.regexMessageEn ?? '');
  pushIfChanged(changes, 'regexMessageFr', prev.regexMessageFr ?? '', next.regexMessageFr ?? '');
  // Help + hint (bilingual).
  pushIfChanged(changes, 'hintEn', prev.hintEn ?? '', next.hintEn ?? '');
  pushIfChanged(changes, 'hintFr', prev.hintFr ?? '', next.hintFr ?? '');
  pushIfChanged(changes, 'helpTitleEn', prev.helpTitleEn ?? '', next.helpTitleEn ?? '');
  pushIfChanged(changes, 'helpTitleFr', prev.helpTitleFr ?? '', next.helpTitleFr ?? '');
  pushIfChanged(changes, 'helpBodyEn', prev.helpBodyEn ?? '', next.helpBodyEn ?? '');
  pushIfChanged(changes, 'helpBodyFr', prev.helpBodyFr ?? '', next.helpBodyFr ?? '');
  // Runtime flags.
  pushIfChanged(changes, 'readOnly', prev.readOnly ?? false, next.readOnly ?? false);
  pushIfChanged(changes, 'hideLabel', prev.hideLabel ?? false, next.hideLabel ?? false);
  pushIfChanged(changes, 'showInReview', prev.showInReview ?? false, next.showInReview ?? false);
  pushIfChanged(
    changes,
    'reviewAsCheckbox',
    prev.reviewAsCheckbox ?? false,
    next.reviewAsCheckbox ?? false,
  );
  pushIfChanged(
    changes,
    'verticalOptions',
    prev.verticalOptions ?? false,
    next.verticalOptions ?? false,
  );
  pushIfChanged(
    changes,
    'advisorRepCodeSelector',
    prev.advisorRepCodeSelector ?? false,
    next.advisorRepCodeSelector ?? false,
  );
  pushIfChanged(
    changes,
    'fundCodeSelector',
    prev.fundCodeSelector ?? false,
    next.fundCodeSelector ?? false,
  );
  pushIfChanged(
    changes,
    'accountSubTypeSelector',
    prev.accountSubTypeSelector ?? false,
    next.accountSubTypeSelector ?? false,
  );
  pushIfChanged(
    changes,
    'jointApplicationSelector',
    prev.jointApplicationSelector ?? false,
    next.jointApplicationSelector ?? false,
  );
  // Invisible in the preview, like every appInfo tag, so the diff is the only place a
  // reviewer can see that what BMO is told about this application has changed.
  pushIfChanged(
    changes,
    'bulkApplicationSelector',
    prev.bulkApplicationSelector ?? false,
    next.bulkApplicationSelector ?? false,
  );
  // Moves an amount between the two appInfo slots while changing nothing visible in the
  // form, so this diff is the only place an approver can see it happen.
  pushIfChanged(
    changes,
    'amountCurrencyFieldId',
    prev.amountCurrencyFieldId ?? null,
    next.amountCurrencyFieldId ?? null,
  );
  pushIfChanged(changes, 'maxRows', prev.maxRows ?? null, next.maxRows ?? null);
  pushIfChanged(changes, 'minAge', prev.minAge ?? null, next.minAge ?? null);
  pushIfChanged(changes, 'maxAge', prev.maxAge ?? null, next.maxAge ?? null);
  pushIfChanged(
    changes,
    'rowsFollowFieldId',
    prev.rowsFollowFieldId ?? '',
    next.rowsFollowFieldId ?? '',
  );
  pushIfChanged(
    changes,
    'mustFollowAllowSame',
    !!prev.mustFollowAllowSame,
    !!next.mustFollowAllowSame,
  );
  pushIfChanged(changes, 'noPastDates', prev.noPastDates ?? false, next.noPastDates ?? false);
  pushIfChanged(changes, 'displayMask', prev.displayMask ?? '', next.displayMask ?? '');
  pushIfChanged(
    changes,
    'mustFollowFieldId',
    prev.mustFollowFieldId ?? null,
    next.mustFollowFieldId ?? null,
  );
  pushIfChanged(changes, 'sourceValue', prev.sourceValue ?? '', next.sourceValue ?? '');
  // PDF mapping (multi-target, order-insensitive).
  pushIfChanged(changes, 'pdfMappings', serializePdfMappings(prev), serializePdfMappings(next));
  // Number formatting + input restrictions.
  pushIfChanged(changes, 'numberFormat', prev.numberFormat ?? '', next.numberFormat ?? '');
  // Which appInfo amount this field feeds. Worth diffing on its own: it changes
  // what BMO receives without changing anything the advisor can see.
  pushIfChanged(changes, 'amountTag', prev.amountTag ?? '', next.amountTag ?? '');
  pushIfChanged(
    changes,
    'inputRestriction',
    prev.inputRestriction ?? '',
    next.inputRestriction ?? '',
  );
  // Address compound.
  pushIfChanged(
    changes,
    'addressAutocomplete',
    prev.addressAutocomplete ?? '',
    next.addressAutocomplete ?? '',
  );
  pushIfChanged(changes, 'addressRole', prev.addressRole ?? '', next.addressRole ?? '');
  // Block-continue gate.
  pushIfChanged(
    changes,
    'blockingValue',
    (prev.blockingValues ?? []).slice().sort().join(', '),
    (next.blockingValues ?? []).slice().sort().join(', '),
  );
  pushIfChanged(
    changes,
    'blockingValueOperator',
    prev.blockingValueOperator ?? '',
    next.blockingValueOperator ?? '',
  );
  pushIfChanged(
    changes,
    'blockContinueAlert',
    prev.blockContinueAlert ?? false,
    next.blockContinueAlert ?? false,
  );
  pushIfChanged(
    changes,
    'blockContinueRestart',
    prev.blockContinueRestart ?? false,
    next.blockContinueRestart ?? false,
  );
  pushIfChanged(
    changes,
    'clearHiddenAnswers',
    prev.clearHiddenAnswers ?? false,
    next.clearHiddenAnswers ?? false,
  );
  pushIfChanged(
    changes,
    'checkBlankRows',
    prev.checkBlankRows ?? false,
    next.checkBlankRows ?? false,
  );
  pushIfChanged(
    changes,
    'blockContinueMessageEn',
    prev.blockContinueMessageEn ?? '',
    next.blockContinueMessageEn ?? '',
  );
  pushIfChanged(
    changes,
    'blockContinueMessageFr',
    prev.blockContinueMessageFr ?? '',
    next.blockContinueMessageFr ?? '',
  );
  // Cross-field wiring.
  pushIfChanged(changes, 'uniquenessGroup', prev.uniquenessGroup ?? '', next.uniquenessGroup ?? '');
  pushIfChanged(
    changes,
    'minSelections',
    prev.minSelections?.toString() ?? '',
    next.minSelections?.toString() ?? '',
  );
  pushIfChanged(
    changes,
    'sourceField',
    formatSourceField(prev.sourceField),
    formatSourceField(next.sourceField),
  );
  // Required-empty override copy.
  pushIfChanged(
    changes,
    'requiredMessageEn',
    prev.requiredMessageEn ?? '',
    next.requiredMessageEn ?? '',
  );
  pushIfChanged(
    changes,
    'requiredMessageFr',
    prev.requiredMessageFr ?? '',
    next.requiredMessageFr ?? '',
  );
  // Per-rule error wording, one line per rule so a reworded message reads as a change.
  pushIfChanged(
    changes,
    'validationMessages',
    formatValidationMessages(prev.validationMessages),
    formatValidationMessages(next.validationMessages),
  );
  // Layout-block styling (StaticText / Paragraph / BulletedList).
  pushIfChanged(changes, 'fontSize', prev.fontSize ?? null, next.fontSize ?? null);
  pushIfChanged(changes, 'color', prev.color ?? '', next.color ?? '');
  pushIfChanged(changes, 'marginTop', prev.marginTop ?? null, next.marginTop ?? null);
  pushIfChanged(changes, 'marginBottom', prev.marginBottom ?? null, next.marginBottom ?? null);
  pushIfChanged(changes, 'fontWeight', prev.fontWeight ?? '', next.fontWeight ?? '');
  pushIfChanged(changes, 'listStyle', prev.listStyle ?? '', next.listStyle ?? '');
  // Notice-specific chrome.
  pushIfChanged(changes, 'backgroundColor', prev.backgroundColor ?? '', next.backgroundColor ?? '');
  pushIfChanged(changes, 'icon', prev.icon ?? '', next.icon ?? '');
  pushIfChanged(changes, 'displayAs', prev.displayAs ?? '', next.displayAs ?? '');
  // Image-specific.
  pushIfChanged(changes, 'imageUrl', prev.imageUrl ?? '', next.imageUrl ?? '');
  pushIfChanged(changes, 'imageWidthSize', prev.imageWidthSize ?? '', next.imageWidthSize ?? '');
  pushIfChanged(changes, 'imageAlignment', prev.imageAlignment ?? '', next.imageAlignment ?? '');
  pushIfChanged(changes, 'captionEn', prev.captionEn ?? '', next.captionEn ?? '');
  pushIfChanged(changes, 'captionFr', prev.captionFr ?? '', next.captionFr ?? '');
  pushIfChanged(changes, 'altEn', prev.altEn ?? '', next.altEn ?? '');
  pushIfChanged(changes, 'altFr', prev.altFr ?? '', next.altFr ?? '');
  // RepeatableRow structure + cross-row sum rule.
  pushJsonChange(
    changes,
    'repeatableTemplate',
    prev.repeatableTemplate ?? null,
    next.repeatableTemplate ?? null,
  );
  pushJsonChange(changes, 'sumValidation', prev.sumValidation ?? null, next.sumValidation ?? null);
  pushOptionsChange(changes, prev.options ?? [], next.options ?? []);
  pushConditionsChange(
    changes,
    'conditions',
    prev.conditions ?? [],
    next.conditions ?? [],
    prev.conditionCombinator ?? null,
    next.conditionCombinator ?? null,
  );
  // Its own row, never merged with the visibility one above — the two rules have
  // opposite polarity and opposite data behaviour, so an approver has to see which
  // of them moved.
  pushConditionsChange(
    changes,
    'disableConditions',
    prev.disableConditions ?? [],
    next.disableConditions ?? [],
    prev.disableConditionCombinator ?? null,
    next.disableConditionCombinator ?? null,
  );
  return changes;
}

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Push a property change onto the list when the two sides differ. Booleans /
 * numbers / null get stringified through {@link formatScalar} so the panel
 * can render "true → false" / "empty → v1" verbatim.
 */
function formatValidationMessages(
  messages: { key: string; en: string; fr?: string | null }[] | null | undefined,
): string {
  return (messages ?? []).map((m) => `${m.key}: ${m.en}${m.fr ? ` / ${m.fr}` : ''}`).join('\n');
}

function pushIfChanged<T>(changes: PropertyChange[], key: string, prev: T, next: T): void {
  if (deepEqual(prev, next)) return;
  changes.push({ key, before: formatScalar(prev), after: formatScalar(next) });
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  return String(v);
}

function formatSourceField(ref: WorkflowField['sourceField']): string {
  if (!ref || !ref.fieldId) return '';
  // The gate and target are part of the prefill: changing either is a change to diff.
  const conditions = ref.conditions ?? [];
  const gate =
    conditions.length > 0
      ? ` when ${ref.conditionCombinator ?? 'all'}(${conditions
          // The connector is part of the gate: moving a row between the and- and
          // or-groups changes when the prefill fires, so it has to be visible here.
          .map(
            (c) => `${c.fieldId} ${c.operator} ${c.value}${c.connector ? ` [${c.connector}]` : ''}`,
          )
          .join(', ')})`
      : '';
  const target = ref.targetInstance != null ? ` → #${ref.targetInstance + 1}` : '';
  return `${ref.stepId}:${ref.fieldId}${gate}${target}`;
}

/**
 * Options list diff — represented as a single "options" property change
 * carrying compact before/after summaries (e.g. "A, B, C" vs "A, B, C, D").
 * The panel formats this as a one-line change rather than a nested list,
 * because 90% of option edits are add-one / rename-one / reword-label.
 *
 * Change DETECTION runs against a full serialization so non-label edits
 * (per-item help title/body, revealFieldId / revealSectionId, clearsOnSelect, per-option
 * internalName for PDF mapping, redirect destinations, French labels) still register — even
 * though the SUMMARY shown to the approver is label-only. That keeps the
 * "1 line per property" UX while making sure a "reveal added to option 3"
 * change doesn't slip past.
 */
function pushOptionsChange(
  changes: PropertyChange[],
  prev: readonly SelectOption[],
  next: readonly SelectOption[],
): void {
  if (serializeOptions(prev) === serializeOptions(next)) return;
  changes.push({
    key: 'options',
    before: summariseOptions(prev),
    after: summariseOptions(next),
  });
}

function serializeOptions(options: readonly SelectOption[]): string {
  // Order-preserving so a reorder counts as a change (the runtime renders
  // options in this order). Every leaf property is included so a per-item
  // reveal / help / PDF-mapping edit doesn't get lost under a shared
  // label. Missing fields ride as empty strings so undefined vs "" doesn't
  // flip the diff on unchanged options. pdfMappings sorted so a reorder of
  // per-option checkbox targets doesn't register as a false positive.
  return JSON.stringify(
    options.map((o) => ({
      value: o.value ?? '',
      valueFr: o.valueFr ?? '',
      labelEn: o.labelEn ?? '',
      labelFr: o.labelFr ?? '',
      helpTitleEn: o.helpTitleEn ?? '',
      helpTitleFr: o.helpTitleFr ?? '',
      helpBodyEn: o.helpBodyEn ?? '',
      helpBodyFr: o.helpBodyFr ?? '',
      revealFieldId: o.revealFieldId ?? '',
      // The section borrow — `revealFieldId`'s plural. Lifts a whole block of
      // the form under this option, so re-pointing it (or clearing it) changes
      // far MORE of what the advisor sees than a single-field reveal does.
      // Omitting it here would let the biggest per-option edit available diff
      // as "unchanged".
      revealSectionId: o.revealSectionId ?? '',
      // Card-layout icon key. Included so swapping an option's icon shows up
      // in the approver's version diff — an icon change is a visible change
      // to what the advisor sees, and omitting it here would make the edit
      // silently invisible at review time (same class of miss as the
      // pdfMappings instanceIndex gap fixed on 2026-07-21).
      icon: o.icon ?? '',
      clearsOnSelect: (o.clearsOnSelect ?? []).slice().sort().join(','),
      // Redirect destination + copy. The most consequential per-option edit
      // there is — it decides whether an answer takes the advisor off this
      // workflow entirely — so it must never diff as "unchanged".
      redirectTemplateId: o.redirectTemplateId ?? '',
      redirectUrl: o.redirectUrl ?? '',
      redirectMessageEn: o.redirectMessageEn ?? '',
      redirectMessageFr: o.redirectMessageFr ?? '',
      // "Selected by default" pre-fills the advisor's answer and a language scope hides the
      // option from one client language — both change what the advisor gets, so both diff.
      isDefault: o.isDefault ?? false,
      languages: (o.languages ?? []).slice().sort().join(','),
      pdfMappings: (o.pdfMappings ?? [])
        .map((m) => `${m.pdfEntryId}:${m.acroFieldName}:${m.instanceIndex ?? ''}`)
        .sort()
        .join('|'),
    })),
  );
}

function summariseOptions(options: readonly SelectOption[]): string {
  if (options.length === 0) return '';
  return options.map((o) => o.labelEn || o.value || '—').join(', ');
}

/**
 * Sum-total change for a nested object we don't want to expand per-leaf
 * (step-type-specific configs, repeatable templates, sum-validation rules).
 * Stringifies both sides through JSON so structural equality is stable,
 * then shows a short "changed" placeholder to the approver — they can drill
 * into the step / field for detail. Objects with the same content but
 * different key order will register as different; we accept that noise
 * because our authoring code doesn't reorder keys.
 */
function pushJsonChange(
  changes: PropertyChange[],
  key: string,
  prev: unknown,
  next: unknown,
): void {
  if (deepEqual(prev ?? null, next ?? null)) return;
  changes.push({
    key,
    before: prev == null ? 'none' : 'changed',
    after: next == null ? 'none' : 'changed',
  });
}

/**
 * Conditions list diff — represents visibility gating changes as
 * "conditions changed" with the human-readable count on each side. The
 * panel doesn't drill into which specific rule changed; the approver can
 * hit the peek popover on the step / field itself to see the current
 * rules (that view already exists — `collectStepConditions`). Point here
 * is just to draw attention.
 */
function pushConditionsChange(
  changes: PropertyChange[],
  key: string,
  prev: readonly FileEntryCondition[],
  next: readonly FileEntryCondition[],
  prevCombinator: 'all' | 'any' | null,
  nextCombinator: 'all' | 'any' | null,
): void {
  if (deepEqual(prev, next) && prevCombinator === nextCombinator) return;
  changes.push({
    key,
    before: summariseConditions(prev, prevCombinator),
    after: summariseConditions(next, nextCombinator),
  });
}

function summariseConditions(
  conditions: readonly FileEntryCondition[],
  combinator: 'all' | 'any' | null,
): string {
  if (conditions.length === 0) return 'none';
  const count = `${conditions.length} rule${conditions.length === 1 ? '' : 's'}`;
  // A rule can mix the two: ALL(and-group) && ANY(or-group), each row choosing
  // its group via `connector` and falling back to the combinator. Spell the
  // split out — `deepEqual` above already FLAGS a moved row, but with the plain
  // "(all)" suffix both sides rendered identically, so the approver was shown a
  // change with nothing visibly changed.
  const fallback = combinator === 'any' ? 'or' : 'and';
  const ors = conditions.filter((c) => (c.connector ?? fallback) === 'or').length;
  const ands = conditions.length - ors;
  if (ands > 0 && ors > 0) return `${count} (${ands} and + ${ors} or)`;
  const suffix = combinator === 'any' ? ' (any)' : combinator === 'all' ? ' (all)' : '';
  return `${count}${suffix}`;
}

/** Structural equality via canonical JSON.stringify. Used for shallow
 *  scalars + small arrays / objects — never a bottleneck at this scale. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}
